import { useEffect, useRef, useState } from "react";
import { ASSESSMENT_ATTEMPT_STATUS } from "@/types/assessmentAttemptStatus";
import type {
  AssessmentAttemptStatus,
  AssessmentAttemptStatuses,
} from "@/types/assessmentAttemptStatus";

export type AssessmentStatuses = AssessmentAttemptStatuses;

const LABELS: Record<string, string> = {
  exit_ticket: "Exit Ticket",
  pre_assessment: "Pre-Assessment",
};

const StatusLabel = ({
  status,
}: {
  status: NonNullable<AssessmentAttemptStatus>;
}) => {
  const label =
    status === ASSESSMENT_ATTEMPT_STATUS.COMPLETED
      ? "Completed"
      : status === ASSESSMENT_ATTEMPT_STATUS.IN_PROGRESS
        ? "In Progress"
        : "Not Started";
  const color =
    status === ASSESSMENT_ATTEMPT_STATUS.COMPLETED
      ? "text-[#374151]"
      : status === ASSESSMENT_ATTEMPT_STATUS.IN_PROGRESS
        ? "text-[#F59E0B]"
        : "text-[#9CA3AF]";
  return <span className={`text-sm ${color}`}>{label}</span>;
};

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#056E47"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AssessmentsDropdown = ({
  statuses,
}: {
  statuses: AssessmentStatuses;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const entries = (["exit_ticket", "pre_assessment"] as const).filter(
    (k) => statuses[k] !== null,
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (entries.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block mt-3">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="flex items-center gap-[10px] p-[10px] rounded-[10px] border border-[#E5E7EB] bg-white text-sm text-[#6B7280] hover:border-[#D1D5DB] transition-colors focus:outline-none"
      >
        Assessments
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-10 min-w-[260px] bg-white border border-[#E5E7EB] rounded-[10px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-hidden">
          {entries.map((key, i) => (
            <div
              key={key}
              className={`flex items-center justify-between px-4 py-3 ${i < entries.length - 1 ? "border-b border-[#F3F4F6]" : ""}`}
            >
              <span className="text-sm font-bold text-[#374151]">
                {LABELS[key]}
              </span>
              <StatusLabel status={statuses[key]!} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssessmentsDropdown;
